/**
 * Transport Home Dashboard
 * Displays key metrics: active routes, pending executions, completion rate, avg distance
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, AlertCircle, CheckCircle, Clock, Loader2, Plus } from 'lucide-react';
import { transportApi, type RawMaterialRoute, type RouteExecution } from '../../services/transport/api';

function MetricCard({
  icon: Icon,
  label,
  value,
  unit,
  trend,
  color = 'blue'
}: {
  icon: any;
  label: string;
  value: string | number;
  unit?: string;
  trend?: number;
  color?: string;
}) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    orange: 'bg-orange-50 border-orange-200',
    red: 'bg-red-50 border-red-200'
  };

  const iconColorClasses = {
    blue: 'text-blue-600',
    green: 'text-green-600',
    orange: 'text-orange-600',
    red: 'text-red-600'
  };

  return (
    <div className={`rounded-lg border ${colorClasses[color as keyof typeof colorClasses]} p-6`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-bold text-gray-900">{value}</span>
            {unit && <span className="text-sm text-gray-600">{unit}</span>}
          </p>
          {trend !== undefined && (
            <div className="mt-2 flex items-center gap-1 text-sm text-green-600">
              <TrendingUp size={16} />
              <span>{trend}% vs last period</span>
            </div>
          )}
        </div>
        <Icon className={`${iconColorClasses[color as keyof typeof iconColorClasses]} h-8 w-8`} />
      </div>
    </div>
  );
}

export default function TransportHome() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState({
    activeRoutes: 0,
    totalRoutes: 0,
    pendingExecutions: 0,
    completedToday: 0,
    avgDistance: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        // Fetch routes to calculate metrics
        const routesResponse = await transportApi.listRoutes(0, 100);
        const routes = routesResponse.content || [];

        const activeRoutes = routes.filter((r: RawMaterialRoute) => r.status === 'ACTIVE').length;
        const avgDistance = routes.length > 0
          ? (routes.reduce((sum: number, r: RawMaterialRoute) => sum + (r.totalDistanceKm || 0), 0) / routes.length).toFixed(1)
          : 0;

        setMetrics({
          activeRoutes,
          totalRoutes: routes.length,
          pendingExecutions: 0, // Would be fetched from executions endpoint
          completedToday: 0,
          avgDistance: parseFloat(avgDistance as string)
        });
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load metrics');
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex gap-3">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <p className="text-sm text-red-800">{error}</p>
          </div>
        </div>
      )}

      {/* Metrics Grid */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard
          icon={Clock}
          label="Active Routes"
          value={metrics.activeRoutes}
          unit={`of ${metrics.totalRoutes}`}
          color="blue"
        />
        <MetricCard
          icon={AlertCircle}
          label="Pending Executions"
          value={metrics.pendingExecutions}
          unit="today"
          color="orange"
        />
        <MetricCard
          icon={CheckCircle}
          label="Completed Today"
          value={metrics.completedToday}
          unit="deliveries"
          color="green"
        />
        <MetricCard
          icon={TrendingUp}
          label="Avg Route Distance"
          value={metrics.avgDistance}
          unit="km"
          color="blue"
        />
      </div>

      {/* Quick Actions */}
      <div className="rounded-lg border border-gray-200 bg-white p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          <button
            onClick={() => navigate('/raw-material-transport/routes/create')}
            className="flex items-center gap-2 rounded-lg border border-blue-300 bg-blue-50 px-4 py-3 text-blue-700 hover:bg-blue-100 transition-colors"
          >
            <Plus size={20} />
            <span className="font-medium">Create New Route</span>
          </button>
          <button
            onClick={() => navigate('/raw-material-transport/routes')}
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-gray-700 hover:bg-gray-100 transition-colors"
          >
            <span className="font-medium">View All Routes</span>
          </button>
          <button
            onClick={() => navigate('/raw-material-transport/executions')}
            className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-4 py-3 text-green-700 hover:bg-green-100 transition-colors"
          >
            <CheckCircle size={20} />
            <span className="font-medium">Monitor Deliveries</span>
          </button>
        </div>
      </div>

      {/* Information Card */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-6">
        <h3 className="font-semibold text-blue-900 mb-2">About Raw Material Transport</h3>
        <p className="text-sm text-blue-800">
          This system manages recurring collection routes from multiple gaushalas to manufacturing units.
          Routes are automatically scheduled daily, drivers execute collections, and transactions are
          auto-created in the manufacturing system upon delivery completion.
        </p>
      </div>
    </div>
  );
}
